/**
 * What the operator is looking at, sent with every question to the assistant.
 *
 * "Why is this night so cheap?" asked on the Prices page and on a strategy
 * section are different questions, and the assistant reads the property only
 * through its tools, never the screen. So each question carries the page, the
 * Prices tab, the strategy editor's section (by its slug, the way the address
 * writes it) or the wizard step on screen, whether the screen holds edits that
 * are not saved yet — the assistant reads the saved strategy — and, on the
 * strategy editor alone, whether expert mode is on, which is what decides the
 * sections that editor offers.
 *
 * The server keeps only the values it knows
 * (`optivo-service/src/services/assistant/pageContext.ts`), so a page this
 * build adds and that server does not know is answered rather than refused;
 * `assistant-page-context.parity.test.ts` holds the two lists together. An
 * admin console names no page: the assistant answers about the property, and
 * the consoles are not where the operator works on it.
 */

import { EDITOR_SECTION_BY_KEY } from "../settings/editor-sections";
import { isAdminPage } from "../../navigation/pages";
import { parseRoute } from "../../navigation/routes";
import type { AssistantPageContext } from "../../services/assistantApi";
import type { DraftsViewMode } from "../Drafts";

/** The longest address the server reads. */
export const ASSISTANT_PAGE_PATH_MAX_CHARS = 200;

/** The Prices page's tabs, as the server names them. */
export const ASSISTANT_PRICES_TABS = ["calendar", "priceHistory", "uploadLog"] as const satisfies readonly DraftsViewMode[];

export interface AssistantScreenState {
    /** The strategy editor holds edits not saved yet (`hasUnsavedWork`). */
    unsavedEdits: boolean;
    /** The stored expert-mode preference (`useExpertMode`). */
    expertMode: boolean;
}

/** The page context of a location, as a question carries it. */
export function buildAssistantPageContext(
    location: { pathname: string; search: string },
    screen: AssistantScreenState,
): AssistantPageContext {
    const route = parseRoute(location.pathname, location.search);
    const page = route && !isAdminPage(route.page) ? route.page : null;
    const context: AssistantPageContext = {
        path: location.pathname.slice(0, ASSISTANT_PAGE_PATH_MAX_CHARS),
        page,
        tab: route?.page === "drafts" ? route.tab ?? "calendar" : null,
        section: route?.page === "strategies" && route.section ? EDITOR_SECTION_BY_KEY[route.section].slug : null,
        step: route?.page === "onboarding" ? route.step ?? null : null,
        unsavedEdits: screen.unsavedEdits,
    };
    if (page === "strategies") context.expertMode = screen.expertMode;
    return context;
}
